<script>
	import Button from '$lib/Components/Layout/Button/Button.svelte';
	import Icon from '$lib/Components/Layout/Icon/Icon.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import IndowOne from '$lib/PageSections/IndowOne/IndowOne.svelte';
	import IndowProContact from '$lib/PageSections/IndowProContact/IndowProContact.svelte';
	import IndowProSolutions from '$lib/PageSections/IndowProSolutions/IndowProSolutions.svelte';

	import CommercialFaq from './CommercialFaq.svelte';
	import crossSectionImagge from './images/IndowPRO-2.0-Cross_Section-tape-callouts.svg';
	import SecondaryGlazingComparisonTable from './SecondaryGlazingComparisonTable.svelte';
	import Slideshow from './Slideshow.svelte';

	const explosionImage = {
		sources: {
			webp: '/static/images/solutions/IndowPRO-explosion.webp',
			gif: '/static/images/solutions/IndowPRO-explosion.gif',
		},
		img: { src: '/static/images/solutions/IndowPRO-explosion.gif' },
	};
</script>

<Section>
	<p class="text-left">
		IndowPRO is a new, engineered secondary glazing system delivering high-end
		replacement performance at a fraction of the cost. Gain powerful benefits
		while avoiding demolition, permits, or guest & tenant downtime.
	</p>

	<div
		class="
    mb-6 w-full rounded-lg bg-indow-grey-dark pt-6 pb-10 text-indow-blue
    shadow-lg
  "
	>
		<p class="font-medium tracking-wide text-white uppercase">
			Upgrade Every Key Metric
		</p>
		<div
			class="
     mx-auto flex max-w-6xl flex-col items-center justify-around space-y-8
     text-center
     md:flex-row md:items-baseline md:space-y-0
   "
		>
			<div>
				<div class="text-5xl font-medium">
					STC<br /><span class="text-6xl">50+</span>
				</div>
				<div class="mt-2 text-indow-white">Acoustic Performance</div>
			</div>

			<div>
				<div class="text-4xl font-medium">
					U-VALUE<br /><span class="text-6xl">.125</span>
				</div>
				<div class="mt-2 text-indow-white">Thermal Performance</div>
			</div>

			<div>
				<div class="text-5xl font-medium">
					&lt;0.01<br /><span class="text-6xl">CFM</span>
				</div>
				<div class="mt-2 text-indow-white">Air Infiltration</div>
			</div>
		</div>
	</div>

	<p class="text-left">
		<strong>Superior Acoustic Control:</strong> IndowPRO quiets rooms with
		exceptional and tunable performance. Select the glazing package which
		delivers the optimal combination of window noise attenuation, occupant
		comfort, and investment ROI. Learn more about
		<a href="/how-to/solve-acoustic-issues-in-commercial-buildings"
			>IndowPRO's acoustic performance</a
		>.
	</p>
	<p class="text-left">
		<strong>High-ROI Energy Efficiency:</strong> Stop energy loss right at the glass.
		With glazing options up to R-18, IndowPRO transforms window system performance,
		stabilizing interior temperatures and delivering measurable savings on HVAC expenses.
	</p>
	<p class="text-left">
		<strong>Preservation of Existing Windows:</strong> Our non-destructive, interior-mount
		installation requires no demolition, permits, or alteration to the exterior facade.
		Efficient installations accommodate guests and occupants, minimizing disruption
		and downtime.
	</p>

	<Slideshow />

	<Button href="/about-indow/commercial-sales">Request a Project Review</Button>
</Section>

<Section backgroundColor="blue">
	<h2 class="mb-6 text-left text-indow-grey-dark">
		A Smarter Solution for Every Project Goal
	</h2>
	<ul
		class="
    w-full list-none gap-6 space-y-6
    md:flex
  "
	>
		<li>
			<p>
				<strong>Design Excellence</strong><br />
				Modern performance which honors the building's original fabric. Our reversible
				system is minimally invasive and can be matched to Pantone colors.
			</p>
			<a
				class="flex items-center gap-3 font-medium text-indow-grey-dark"
				href="/how-to/indowpro-for-architects"
			>
				View Architectural Details
				<Icon name="arrow" class="w-5" />
			</a>
		</li>
		<li>
			<p>
				<strong>Engineered Reliability</strong><br />
				Enhances asset value with a rapid ROI by significantly cutting utility costs
				and improving tenant retention. All without the high cost and disruption of
				a replacement.
			</p>
			<a
				class="flex items-center gap-3 font-medium text-indow-grey-dark"
				href="/how-to/commercial-noi"
			>
				See Performance Data
				<Icon name="arrow" class="w-5" />
			</a>
		</li>
		<li>
			<p>
				<strong>Building Value</strong><br />
				Fast execution and profitable project completion. PRO's knock-down design
				slashes shipping & labor costs, and de-risks jobs by ensuring a reliable fit
				even in out-of-square windows.
			</p>
			<a
				class="flex items-center gap-3 font-medium text-indow-grey-dark"
				href="/how-to/install-indowpro"
			>
				View Installation Guide
				<Icon name="arrow" class="w-5" />
			</a>
		</li>
	</ul>
</Section>

<Section>
	<h2 class="text-left">Secondary Glazing, Redefined</h2>

	<Image src={crossSectionImagge} />
	<h3 class="mb-6">Seamless, Efficient Installations</h3>

	<p class="text-left">
		IndowPRO installs on the interior, right inside your existing window frames,
		to create a high-performance insulating and acoustic barrier.
	</p>
	<p class="text-left">
		Our patent-pending, modular frames ship disassembled to reduce logistics
		costs, then snap together quickly on-site for fast, non-disruptive
		installations. The system reliably absorbs frame irregularities to ensure an
		airtight, aesthetically clean, and fully reversible seal even in
		out-of-square buildings.
	</p>
	<p class="text-left">
		IndowPRO is compatible with a wide variety of advanced glazings, allowing
		you to select the ideal option for your project's specific noise reduction,
		comfort, or efficiency goals.
	</p>

	<h3>Comparing Secondary Glazing Options</h3>
	<SecondaryGlazingComparisonTable />

	<div
		class="mb-12 rounded-lg bg-indow-grey-dark p-6 text-indow-white shadow-xl"
	>
		<p class="mb-3 text-left text-xl font-medium">A Note on Operability</p>
		<p class="text-left">
			IndowPRO is a fixed system designed for maximum performance. While the
			primary window is not operable with the system installed, the glazing can
			be removed to access the primary window for cleaning and maintenance.
		</p>
	</div>

	<Image src={explosionImage} />
	<p>
		<a
			href="/static/routes/solutions/commercial-window-inserts/IndowPRO_Product_Data_Sheet_Q3_25.pdf"
			>Download Product Data Sheet (pdf)</a
		>
	</p>
</Section>

<IndowOne />

<IndowProSolutions />

<IndowProContact />

<Section><CommercialFaq /></Section>
